/**
 * RM Decision Log table (Reporting page's RM Decision Log tab, RM-only).
 *
 * One row per Solution — no create/delete, only edit. `GET
 * /api/reports/decision-log` returns the whole (securely scoped) result set in
 * one call — see src/api/decision-log.ts for why. So sort, filter, and
 * pagination all happen CLIENT-SIDE over that one in-memory array, matching
 * PipelineReportTable.tsx's convention.
 *
 * Account, Solution, Sales Stage, and Resourcing Stage are read-only display
 * cells. Notes / Decisions / Next Actions are inline, per-cell, click-to-edit
 * — every editable cell is optimistic: it flips to the new value the instant
 * the edit commits, and rolls back with an error toast if the PATCH fails.
 * The optimism lives in each cell's own local state (not the TanStack Query
 * cache), which resyncs to the server value via a `useEffect` the moment the
 * invalidated query refetches — same pattern as PipelineReportTable.tsx.
 */

import { Loader2, NotebookText, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { DecisionLogEntryUpdateInput, DecisionLogRow } from "@/hooks/use-decision-log";
import { useDecisionLog, useUpdateDecisionLogEntry } from "@/hooks/use-decision-log";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;

/** Ref callback that focuses its element the instant it mounts — see the
 * identical helper + rationale in PipelineReportTable.tsx. */
function focusOnMount(node: HTMLElement | null): void {
  node?.focus();
}

const RESOURCING_STAGE_LABEL: Record<DecisionLogRow["resourcingStage"], string> = {
  Active: "Active",
  Pending: "Pending",
  Closed: "Closed",
};

// ── Sorting ─────────────────────────────────────────────────────────────────────

type SortColumn = "accountName" | "solutionName" | "salesStage" | "resourcingStage";

interface SortState {
  column: SortColumn;
  direction: "asc" | "desc";
}

const DEFAULT_SORT: SortState = { column: "accountName", direction: "asc" };

/** Matches the collator config in src/lib/option-search.ts — see the identical
 * note in PipelineReportTable.tsx. */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** Nulls always sort LAST, in both ascending and descending order — same
 * rationale as PipelineReportTable.tsx's compareNullableString. */
function compareNullableString(a: string | null, b: string | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return COLLATOR.compare(a, b) * dir;
}

function compareRows(a: DecisionLogRow, b: DecisionLogRow, sort: SortState): number {
  const dir: 1 | -1 = sort.direction === "asc" ? 1 : -1;
  switch (sort.column) {
    case "accountName":
      return COLLATOR.compare(a.accountName, b.accountName) * dir;
    case "solutionName":
      return COLLATOR.compare(a.solutionName, b.solutionName) * dir;
    case "salesStage":
      return compareNullableString(a.salesStage, b.salesStage, dir);
    case "resourcingStage":
      return COLLATOR.compare(a.resourcingStage, b.resourcingStage) * dir;
    default:
      return 0;
  }
}

function sortRows(rows: DecisionLogRow[], sort: SortState): DecisionLogRow[] {
  return [...rows].sort((a, b) => compareRows(a, b, sort));
}

/** True when `term` (case-insensitive, trimmed) appears in ANY column's
 * displayed text for this row. An empty term matches everything. */
function matchesGlobalSearch(row: DecisionLogRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === "") return true;
  const haystack = [
    row.accountName,
    row.solutionName,
    row.salesStage,
    RESOURCING_STAGE_LABEL[row.resourcingStage],
    row.notes,
    row.decisions,
    row.nextActions,
  ]
    .filter((v): v is string => v !== null && v !== undefined)
    .join("\n")
    .toLowerCase();
  return haystack.includes(needle);
}

// ── Per-cell save plumbing ───────────────────────────────────────────────────────

/** One mutation instance per cell, so `pending` reflects only that cell's own
 * save — not every inline edit on the page. */
function useDecisionLogFieldSave(solutionId: string) {
  const update = useUpdateDecisionLogEntry();
  function save(patch: DecisionLogEntryUpdateInput, rollback: () => void) {
    update.mutate({ solutionId, patch }, { onError: rollback });
  }
  return { save, pending: update.isPending };
}

// ── Generic click-to-edit shell ─────────────────────────────────────────────────
//
// Identical shell to PipelineReportTable.tsx's InlineEditCell — duplicated
// rather than shared because it's small, has no state of its own beyond the
// editing toggle, and each table's cells reach into their own field-save hook.

function InlineEditCell({
  pending,
  display,
  renderEditor,
}: {
  pending: boolean;
  display: React.ReactNode;
  renderEditor: (close: () => void) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return <div className="min-w-0">{renderEditor(() => setEditing(false))}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={pending}
      className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left transition-colors hover:bg-muted/60 disabled:cursor-wait disabled:opacity-60"
    >
      <span className="min-w-0 flex-1 break-words">{display}</span>
      {pending && <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />}
    </button>
  );
}

// ── Free-text editable cell (Notes / Decisions / Next Actions) ──────────────────

function TextAreaEditor({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const cancelledRef = useRef(false);
  return (
    <Textarea
      ref={focusOnMount}
      rows={2}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (!cancelledRef.current) onCommit(value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          cancelledRef.current = true;
          onCancel();
        }
      }}
      className="min-h-16 text-sm"
    />
  );
}

function TextFieldCell({
  solutionId,
  field,
  current,
}: {
  solutionId: string;
  field: keyof DecisionLogEntryUpdateInput;
  current: string | null;
}) {
  const [local, setLocal] = useState(current);
  useEffect(() => setLocal(current), [current]);
  const { save, pending } = useDecisionLogFieldSave(solutionId);

  return (
    <InlineEditCell
      pending={pending}
      display={
        local ? (
          <span className="break-words">{local}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      }
      renderEditor={(close) => (
        <TextAreaEditor
          initial={local ?? ""}
          onCancel={close}
          onCommit={(raw) => {
            const next = raw.trim() === "" ? null : raw;
            if (next === local) {
              close();
              return;
            }
            const prev = local;
            setLocal(next);
            close();
            save({ [field]: next }, () => setLocal(prev));
          }}
        />
      )}
    />
  );
}

// ── Sortable column header ───────────────────────────────────────────────────────

function SortableHeader({
  column,
  label,
  sort,
  onSort,
  className,
}: {
  column: SortColumn;
  label: string;
  sort: SortState;
  onSort: (column: SortColumn) => void;
  className?: string;
}) {
  const active = sort.column === column;
  return (
    <th
      className={cn(
        "sticky top-0 z-20 whitespace-nowrap bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {label}
      </button>
    </th>
  );
}

// ── Skeleton rows ─────────────────────────────────────────────────────────────────

const SKELETON_ROW_IDS = ["sk-r1", "sk-r2", "sk-r3", "sk-r4", "sk-r5"] as const;
const SKELETON_COLUMNS = [
  "account",
  "solution",
  "salesStage",
  "resourcingStage",
  "notes",
  "decisions",
  "nextActions",
] as const;

function DecisionLogSkeletonRows() {
  return (
    <>
      {SKELETON_ROW_IDS.map((rowId) => (
        <tr key={rowId} className="border-b last:border-b-0">
          {SKELETON_COLUMNS.map((colKey) => (
            <td key={colKey} className="px-3 py-2.5">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ── Body row ──────────────────────────────────────────────────────────────────────

function DecisionLogBodyRow({ row }: { row: DecisionLogRow }) {
  return (
    <tr className="border-b border-border/50 last:border-0 align-top">
      <td
        className="sticky left-0 z-10 w-[140px] break-words bg-background px-3 py-2.5 font-medium"
        title={row.accountName}
      >
        {row.accountName}
      </td>
      <td
        className="sticky left-[140px] z-10 w-[180px] break-words border-r bg-background px-3 py-2.5"
        title={row.solutionName}
      >
        {row.solutionName}
      </td>
      <td className="px-3 py-2.5">{row.salesStage ?? "—"}</td>
      <td className="px-3 py-2.5">{RESOURCING_STAGE_LABEL[row.resourcingStage]}</td>
      <td className="max-w-[240px] px-3 py-2.5">
        <TextFieldCell solutionId={row.solutionId} field="notes" current={row.notes} />
      </td>
      <td className="max-w-[240px] px-3 py-2.5">
        <TextFieldCell solutionId={row.solutionId} field="decisions" current={row.decisions} />
      </td>
      <td className="max-w-[240px] px-3 py-2.5">
        <TextFieldCell solutionId={row.solutionId} field="nextActions" current={row.nextActions} />
      </td>
    </tr>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────────

export function DecisionLogTable() {
  const { data, isLoading, isFetching, isError, error, refetch } = useDecisionLog();
  const allRows = useMemo(() => data?.data ?? [], [data]);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(search, 250);

  const searched = useMemo(
    () => allRows.filter((r) => matchesGlobalSearch(r, debouncedSearch)),
    [allRows, debouncedSearch],
  );
  const sorted = useMemo(() => sortRows(searched, sort), [searched, sort]);

  // Search or sort changing invalidates the current page position — same
  // render-time adjustment pattern as PipelineReportTable.tsx (no useEffect).
  const resetKeyRef = useRef({ debouncedSearch, sort });
  if (
    resetKeyRef.current.debouncedSearch !== debouncedSearch ||
    resetKeyRef.current.sort !== sort
  ) {
    resetKeyRef.current = { debouncedSearch, sort };
    if (page !== 1) setPage(1);
  }

  function handleSort(column: SortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load the RM Decision Log."}
        onRetry={() => refetch()}
      />
    );
  }

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount);
  const startIdx = (clampedPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <div className="space-y-3">
      <div className="relative w-64">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search all columns…"
          className="h-9 pl-8 text-sm"
        />
      </div>

      <div className="max-h-[70vh] overflow-auto rounded-lg border">
        <table className="w-full min-w-[1100px] table-fixed text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableHeader
                column="accountName"
                label="Account"
                sort={sort}
                onSort={handleSort}
                className="left-0 z-30 w-[140px]"
              />
              <SortableHeader
                column="solutionName"
                label="Solution"
                sort={sort}
                onSort={handleSort}
                className="left-[140px] z-30 w-[180px] border-r"
              />
              <SortableHeader
                column="salesStage"
                label="Sales Stage"
                sort={sort}
                onSort={handleSort}
                className="w-[120px]"
              />
              <SortableHeader
                column="resourcingStage"
                label="Resourcing Stage"
                sort={sort}
                onSort={handleSort}
                className="w-[130px]"
              />
              <th className="sticky top-0 z-20 bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                Notes
              </th>
              <th className="sticky top-0 z-20 bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                Decisions
              </th>
              <th className="sticky top-0 z-20 bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                Next Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <DecisionLogSkeletonRows />
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={SKELETON_COLUMNS.length}>
                  <EmptyState
                    icon={NotebookText}
                    title="No Solutions"
                    description={
                      allRows.length === 0
                        ? "No Solutions to log yet."
                        : "No Solutions match the current search."
                    }
                  />
                </td>
              </tr>
            ) : (
              pageRows.map((row) => <DecisionLogBodyRow key={row.solutionId} row={row} />)
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && !isError && total > 0 && (
        <CursorPagination
          total={total}
          page={clampedPage}
          limit={PAGE_SIZE}
          hasNext={clampedPage < pageCount}
          hasPrev={clampedPage > 1}
          onNext={() => setPage((p) => p + 1)}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          loading={isFetching}
          itemLabel="Solutions"
        />
      )}
    </div>
  );
}
